import type { AbVariant, Criteria, Experiment } from '@snapchat/mw-contentful-schema';
import { BrowserFeaturesContext } from '@snapchat/snap-design-system-marketing';
import kebabCase from 'lodash-es/kebabCase';
import type { RefObject } from 'react';
import { useCallback, useContext } from 'react';

import { AppContext } from '../../AppContext';
import { logEvent, logInfo, SubscribedEventType } from '../../helpers/logging';
import type { AbExperimentDecider } from '../../hooks/useAbExperiments';
import { useAbExperiments } from '../../hooks/useAbExperiments';
import { type SingleCallbackFn, useSingleCallback } from '../../hooks/useSingleCallback';
import type { UserBusinessProfileDecider } from '../../hooks/useUserBusinessProfile';
import { useUserBusinessProfile } from '../../hooks/useUserBusinessProfile';
import type { UserSnapProfileDecider } from '../../hooks/useUserSnapProfile';
import { useUserSnapProfile } from '../../hooks/useUserSnapProfile';
import type { ContentfulSysProps } from '../../types/contentful';
import type { Personas } from '../../types/personas';
import type { CookieManager } from '../../utils/cookies/CookieManager';
import { getPersonasCookie, setPersonasCookie } from '../../utils/persona/personaUtils';
import type { BlockShallowWithContentIds } from '../Block';
import type { PageShallowDataProps } from '../Page';
import { extractUtmParameters } from './utils';

// =================================================================================================
// Controls
// =================================================================================================
const printDebugStatements = false;

function printDebugSkip(
  analyticsId: string | undefined,
  what: string | number,
  where: Array<string> | Array<number> | undefined,
  action: string | undefined
) {
  if (!printDebugStatements) return;
  analyticsId ??= 'synthetic';
  action ??= 'Allow';

  console.debug(
    `Criteria not applicable for arm '${analyticsId}' because of check ` +
      `'${what}' in '${where}' has action '${action}'.`
  );
}

// =================================================================================================
// Interfaces
// =================================================================================================

export interface CriteriaDecisionProps {
  /**
   * Snap standard locale code in format language-COUNTRY Where langauge is one of the
   * https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes And country is one of the alpha-2
   * https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes codes
   */
  language: string;

  /** 2-digit country code as in https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes */
  country: string;

  /**
   * One of "Android", "Chrome OS", "Fuchsia", "iOS", "Linux", "macOS", "Windows", or "Unknown". See
   * spec: https://wicg.github.io/ua-client-hints/#sec-ch-ua-platform
   */
  platform: 'Android' | 'Chrome OS' | 'Fuchsia' | 'iOS' | 'Linux' | 'macOS' | 'Windows' | 'Unknown';

  /**
   * Whether the device is mobile or not. Based on "mobile" user hints header. See spec:
   * https://wicg.github.io/ua-client-hints/#sec-ch-ua-mobile
   */
  deviceType: 'Mobile' | 'Non-Mobile' | 'Unknown';

  /**
   * UTM Parameters that are present in the URL. Currently we only check for utm_source, utm_medium,
   * utm_campaign, utm_content
   */
  utmParams: Record<string, string>;
}

// TODO: This shouldn't extend CriteriaDecisionProps at all.
export interface ExperimentDecisionProps extends CriteriaDecisionProps {
  cookieManager: CookieManager;
  getCurrentUrl: () => string;
  singleCallback: SingleCallbackFn;
  decideAbExperiment: AbExperimentDecider;
  getUserSnapProfile: UserSnapProfileDecider;
  getUserBusinessProfile: UserBusinessProfileDecider;
  personasRef?: RefObject<Personas | undefined>;
  updatePersonas?: (personas: Personas) => void;
}

export type ReplacementData =
  | ContentfulSysProps
  | BlockShallowWithContentIds
  | PageShallowDataProps;

export type ReplacementMap = Partial<Record<string, ReplacementData>>;

interface ExperimentResult<T> {
  decision: T;
  experimentId?: string;
  variantId?: string;
  replacements?: ReplacementMap;
}

/**
 * Finds the first experiment arm that matches its criteria, and if all fail returns the default.
 *
 * In absence of an experiment, returns value.
 */
export type ExperimentDecider = <T extends ContentfulSysProps>(
  value: T | Experiment,
  options: {
    logImpression: boolean;
  }
) => ExperimentResult<T>;

/** Returns TRUE if the passed-in value MATCHES the criteria. */
export type CriteriaChecker = (criteria: Criteria | null | undefined) => boolean;

// =================================================================================================
// Public Experiment API
// =================================================================================================

export interface UseExperiment {
  decideExperiment: ExperimentDecider;
  // TODO: Move CriteriaChecker into a different hook (useCriteria).
  /** Does NOT check business personas or snap personas. Only checks the other criteria. */
  checkCriteria: CriteriaChecker;
}

export function useExperiment(): UseExperiment {
  const { cookieManager, currentLocale, userLocation, getCurrentUrl, personasRef, updatePersonas } =
    useContext(AppContext);
  const browserFeatures = useContext(BrowserFeaturesContext);
  const singleCallback = useSingleCallback();
  const decideAbExperiment = useAbExperiments();
  const getUserSnapProfile = useUserSnapProfile();
  const getUserBusinessProfile = useUserBusinessProfile();

  const language = currentLocale ?? 'Unknown';
  const country = userLocation.country ?? 'Unknown';
  const platform = browserFeatures.getLowEntropyHints().platform ?? 'Unknown';
  const deviceType = browserFeatures.getLowEntropyHints().isMobile ? 'Mobile' : 'Non-Mobile';
  const utmParams = extractUtmParameters(getCurrentUrl());

  const checkCriteria = useCallback(
    (value: Criteria | null | undefined) => {
      return doesPassCriteria(value, { language, country, platform, deviceType, utmParams });
    },
    [language, country, platform, deviceType, utmParams]
  );

  const decideExperiment = useCallback<ExperimentDecider>(
    (value, options) => {
      const context: ExperimentDecisionProps = {
        language,
        country,
        platform,
        deviceType,
        singleCallback,
        getCurrentUrl,
        decideAbExperiment,
        utmParams,
        getUserSnapProfile,
        getUserBusinessProfile,
        personasRef,
        updatePersonas,
        cookieManager,
      };

      return decideExperimentImpl(value, context, options);
    },
    [
      language,
      country,
      platform,
      deviceType,
      singleCallback,
      decideAbExperiment,
      getCurrentUrl,
      utmParams,
      getUserSnapProfile,
      getUserBusinessProfile,
      personasRef,
      updatePersonas,
      cookieManager,
    ]
  );

  return {
    decideExperiment,
    checkCriteria,
  };
}

/**
 * Returns the default value of an experiment or the only value without the experiment wrapper.
 *
 * NOTE: Use this very sparingly. This is only applicable in situations where nothing sensitive can
 * be shown to the user. I.e. If an experiment has a filter to not show sensitive material in Saudi
 * Arabia, we cannot use this method if it can show bad material to our users.
 *
 * Otherwise use the {@link decideExperimentImpl} and provide the correct context.
 */
export function getExperimentDefault<T extends ContentfulSysProps>(value: T | Experiment): T {
  // If not an experiment, return.
  if (value.__typename !== 'Experiment') {
    return value as T;
  }
  const experiment = value as Experiment;
  return experiment.defaultReference as unknown as T;
}

/**
 * Returns whether the action check fails.
 *
 * Usage:
 *
 * If (doesFailCheck(check)) { fail condition here }
 */
const doesFailCheck = (array: string[] | undefined, value: string, action?: string): boolean => {
  action ??= 'Allow'; // Works for nulls.

  // Fail if we have an allow-list, and current value isn't on it.
  if (action === 'Allow' && array && !array.includes(value)) return true;

  // Fail if we have a block-list and the current value is on it.
  if (action === 'Deny' && array?.includes(value)) return true;
  // Otherwise return 'false' as in 'check did not fail'.
  return false;
};

export function doesPassCriteria(
  criteria: Criteria | null | undefined,
  props: CriteriaDecisionProps,
  analyticsId?: string
): boolean {
  if (!criteria) return true;

  // Check countries.
  if (doesFailCheck(criteria.countries, props.country, criteria.countryAction)) {
    printDebugSkip(analyticsId, props.country, criteria.countries, criteria.countryAction);
    return false;
  }

  // Check languages.
  if (doesFailCheck(criteria.languages, props.language, criteria.languageAction)) {
    printDebugSkip(analyticsId, props.language, criteria.languages, criteria.languageAction);
    return false;
  }

  // Check platforms.
  if (doesFailCheck(criteria.platforms, props.platform, criteria.platformAction)) {
    printDebugSkip(analyticsId, props.platform, criteria.platforms, criteria.platformAction);
    return false;
  }

  // Check devices.
  if (doesFailCheck(criteria.devices, props.deviceType, criteria.deviceAction)) {
    printDebugSkip(analyticsId, props.deviceType, criteria.devices, criteria.deviceAction);
    return false;
  }

  // Check UTM parameters.
  // Every query param in the criteria must have at least one match (for allow-list) or none in deny-list
  if (criteria.utmParams) {
    // iterate over each criteria's utm parameter and check if it passes the criteria
    for (const [utmParam, value] of Object.entries(criteria.utmParams)) {
      if (doesFailCheck(value, props.utmParams[utmParam] ?? '', criteria.utmParamsAction)) {
        printDebugSkip(
          analyticsId,
          props.utmParams[utmParam] ?? '',
          // need to cast to Record<string, string[]> becaues its unknown in contentful-types.ts
          (criteria.utmParams as Record<string, string[]>)[utmParam],
          criteria.utmParamsAction
        );

        return false;
      }
    }
  }

  // If none of the checks failed, then the criteria has passed.
  return true;
}

/**
 * Evaluates the experiment and finds the first arm for which criteria passes. If no arms pass, uses
 * the default reference.
 */
export function decideExperimentImpl<T extends ContentfulSysProps>(
  value: T | Experiment,
  props: ExperimentDecisionProps,
  options: Parameters<ExperimentDecider>[1]
): ExperimentResult<T> {
  // If not an experiment, return.
  if (value.__typename !== 'Experiment') {
    // we fire this log event to show that there was no experiment on this page
    // this is useful for clearing experiment data on certain listeners (like Blizzard)
    // Google and GoogleCloud listeners ignore this event
    options.logImpression &&
      logEvent({
        subscribedEventType: SubscribedEventType.EXPERIMENT_IMPRESSION,
        experimentId: undefined,
        variantId: undefined,
        // TODO: Normally, the locale is automatically added to all log events by a logging context provider
        // set in App.tsx. Unfortunately, this context provider is not attached yet by the time this runs due
        // to a bug with the way the context provider is attached. We can remove this once the bug is fixed.
        // https://jira.sc-corp.net/browse/WEBP-11200
        context: { locale: props.language },
      });

    return { decision: value as T };
  }
  const experiment = value as Experiment;

  let chosenArmAnalyticsId = 'default';
  let reference = experiment.defaultReference;
  const replacements: ReplacementMap = {};
  const singleCallback = props.singleCallback;
  // use this to prevent logging an exposure for the default (control) if business personas are being fetched
  let isThinking = false;

  for (const arm of experiment.experimentArmsCollection?.items ?? []) {
    if (!arm.criteria) continue;

    // Check other criteria (not including business personas or snap personas)
    const passCriteria = doesPassCriteria(arm.criteria, props, arm.analyticsId);

    // Check snap personas criteria
    const snapPersonasCriteria = arm.criteria?.snapPersonas;

    // Check business personas criteria
    const businessPersonasCriteria = arm.criteria?.businessPersonas;
    const businessPersonasMatchAll = arm.criteria?.businessPersonasMatchAll;

    const hasBusinessPersonasCriteria =
      !!businessPersonasCriteria?.length || businessPersonasMatchAll;

    const hasSnapPersonasCriteria = !!snapPersonasCriteria?.length;

    // If no personas criteria and no other criteria pass, skip this arm
    if (!passCriteria && !hasBusinessPersonasCriteria && !hasSnapPersonasCriteria) continue;

    // If business personas criteria exist on this arm, check if the user's business persona matches the criteria
    // This block of code only looks at the business personas criteria and not the other criteria.
    // The purpose of this block is to short circuit if the user's business personas do not match the criteria.
    if (hasBusinessPersonasCriteria) {
      // If still no user profile found, fetch it
      const userBusinessProfile = props.getUserBusinessProfile();

      if (userBusinessProfile.isThinking) {
        // if the user's business profile is still being fetched, we set isThinking to true
        // so that we can prevent logging an exposure for the default (control) if business personas are being fetched
        isThinking = true;
        break;
      }

      const userBusinessProfileDecision = userBusinessProfile.decision;

      // Get the existing personas values from the cookie or the app state
      const existingPersonasValues =
        getPersonasCookie(props.cookieManager) || props.personasRef?.current;

      // Pass the updated personas back to the caller so that the server can use it to set the initial APP_STATE
      // We only want to update the biz personas, so we merge the existing personas with the new biz personas
      const updatedPersonas = {
        ...existingPersonasValues,
        biz: userBusinessProfileDecision,
      } as Personas;

      props.updatePersonas?.(updatedPersonas);
      setPersonasCookie(props.cookieManager, updatedPersonas);

      // If a business profile was NOT found, i.e. `undefined` or `null`, skip this arm
      if (!userBusinessProfileDecision || userBusinessProfileDecision.length === 0) {
        continue;
      }

      // The action field can be undefined in the criteria, so we default to 'Allow'
      const businessPersonaAction = arm.criteria?.businessPersonaAction ?? 'Allow';

      // If matching ALL business personas
      if (businessPersonasMatchAll) {
        // If the action is to deny, skip this arm
        if (businessPersonaAction === 'Deny') {
          continue;
        }
        // otherwise, continue down this code and check the AB Experiments
      }
      // If matching ONLY selected business personas
      else {
        // Check if the user's business personas match any of the criteria
        const doesMatchCriteria = userBusinessProfileDecision.some(decision =>
          businessPersonasCriteria?.includes(decision)
        );

        let doesFailCheck = false;

        // Fail if we have an allow-list, and current value isn't on it
        if (businessPersonaAction === 'Allow' && !doesMatchCriteria) {
          doesFailCheck = true;
        }

        // Fail if we have a block-list and the current value is on it
        if (businessPersonaAction === 'Deny' && doesMatchCriteria) {
          doesFailCheck = true;
        }

        // If the user's business personas do not match the criteria, skip this arm
        if (doesFailCheck) {
          userBusinessProfileDecision.forEach(decision => {
            printDebugSkip(
              arm.analyticsId,
              decision,
              businessPersonasCriteria,
              businessPersonaAction
            );
          });
          continue;
        }
      }
      // If we made it this far, the user's business personas match the criteria
      // so we can continue down this code and check the AB Experiments.
      // The reference and chosenArmAnalyticsId will be set in the next block of code.
    }

    // If snap personas criteria exist on this arm, check if the user's snap persona matches the criteria
    // This block of code only looks at the snap personas criteria and not the other criteria.
    // The purpose of this block is to short circuit if the user's snap personas do not match the criteria.
    if (hasSnapPersonasCriteria) {
      const snapPersonasDecision = props.getUserSnapProfile();

      // If a snap profile was NOT matched, skip this arm
      if (!snapPersonasDecision || snapPersonasDecision.length === 0) {
        continue;
      }

      // The action field can be undefined in the criteria, so we default to 'Allow'
      const snapPersonaAction = arm.criteria?.snapPersonaAction ?? 'Allow';

      // Check if the user's snap personas match any of the criteria
      const doesMatchCriteria = snapPersonasDecision.some(decision =>
        snapPersonasCriteria?.includes(decision)
      );

      let doesFailCheck = false;

      // Fail if we have an allow-list, and current value isn't on it
      if (snapPersonaAction === 'Allow' && !doesMatchCriteria) {
        doesFailCheck = true;
      }

      // Fail if we have a block-list and the current value is on it
      if (snapPersonaAction === 'Deny' && doesMatchCriteria) {
        doesFailCheck = true;
      }

      // If the user's snap personas do not match the criteria, skip this arm
      if (doesFailCheck) {
        snapPersonasDecision.forEach(decision => {
          printDebugSkip(arm.analyticsId, decision, snapPersonasCriteria, snapPersonaAction);
        });
        continue;
      }

      // If we made it this far, the user's snap personas match the criteria
      // so we can continue down this code and check the business personas and AB Experiments.
      // The reference and chosenArmAnalyticsId will be set further down in this code.
    }

    // If the other criteria pass, use this arm's reference and continue to check AB Experiments
    reference = arm.reference;
    chosenArmAnalyticsId = arm.analyticsId ?? chosenArmAnalyticsId;

    const armHasAbExperiment = (arm.abExperimentsCollection?.items.length ?? 0) > 0;

    if (armHasAbExperiment && arm.abExperimentsCollection?.items.length) {
      const abExperiments = arm.abExperimentsCollection.items;

      // for every experiment on the page
      for (const abExperiment of abExperiments) {
        const entryToReplace = abExperiment.reference?.sys.id;
        let madeReplacement = false;
        if (!entryToReplace) continue;
        const { isThinking, decision: experimentArm } = props.decideAbExperiment<AbVariant>({
          seed: abExperiment.seed ?? abExperiment.sys.id,
          variants: abExperiment.abVariantsCollection?.items,
        });

        if (experimentArm) {
          // If replacement is a Page, this is a page swap so just change the reference to the replacement
          if (experimentArm.replacement?.__typename === 'Page') {
            reference = experimentArm.replacement;
            // The replacement is at the entry level, and the replacement entry exists
          } else if (experimentArm.replacement) {
            replacements[entryToReplace] = {
              ...experimentArm.replacement,
            };
            // The replacement is at the entry level, but the replacement entry does not exist so we set it to undefined
            // this will let the components know to not render the entryToReplace
          } else {
            replacements[entryToReplace] = undefined;
          }

          // Log Exposure for the variant swap
          singleCallback({
            key: 'useExperimentVariantSwap',
            callback: () =>
              logInfo({
                eventCategory: 'Experiment',
                eventAction: 'experimentExposure',
                eventLabel: `${chosenArmAnalyticsId}:${abExperiment.analyticsId}:${experimentArm.analyticsId}`,
                context: { locale: props.language },
              }),
            dependencies: [
              chosenArmAnalyticsId,
              abExperiment.analyticsId,
              experimentArm.analyticsId,
            ],
          });

          madeReplacement = true;
        }

        // no replacements were made, so we log an exposure for the default (control)
        if (!isThinking && !madeReplacement) {
          // Log event into GA to record that experiment decisions happen. This also goes to Blizard.
          singleCallback({
            key: 'useExperimentDefault',
            callback: () =>
              logInfo({
                eventCategory: 'Experiment',
                eventAction: 'experimentExposure',
                eventLabel: `${chosenArmAnalyticsId}:${abExperiment.analyticsId}:default`,
                context: { locale: props.language },
              }),
            dependencies: [chosenArmAnalyticsId, abExperiment.analyticsId],
          });
        }
      }
    }
    break;
  }

  // if the user's business profile is still being fetched, we do not log an exposure for the default (control)
  if (isThinking) {
    return { decision: reference as unknown as T };
  }

  // Log event into GA to record that experiment decisions happen. This also goes to Blizard.
  singleCallback({
    key: 'useExperiment',
    callback: () =>
      logInfo({
        eventCategory: 'Experiment',
        eventAction: 'useExperiment',
        eventLabel: `${kebabCase(experiment.analyticsId ?? '')}:${kebabCase(chosenArmAnalyticsId)}`,
        context: { locale: props.language },
      }),
    dependencies: [experiment.analyticsId, chosenArmAnalyticsId],
  });

  // Log event through GTag into Google Optimize for report generation.
  options.logImpression &&
    singleCallback({
      key: 'experimentImpression',
      callback: () =>
        logEvent({
          subscribedEventType: SubscribedEventType.EXPERIMENT_IMPRESSION,
          experimentId: experiment.analyticsId ?? 'Unknown',
          variantId: chosenArmAnalyticsId,
          context: { locale: props.language },
        }),
      dependencies: [experiment.analyticsId, chosenArmAnalyticsId],
    });

  return {
    decision: reference as unknown as T,
    experimentId: experiment.analyticsId,
    variantId: chosenArmAnalyticsId,
    replacements,
  };
}
